/* eslint class-methods-use-this: 0, no-restricted-syntax: 0, no-await-in-loop: 0, no-param-reassign: 0, max-len: 0, no-async-promise-executor: 0 */
import { DirectoryGroupingConsts } from 'client/util/Constants';
import { capitalize } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';

/**
 * Extend base class to add specific functionality for User Group Suggestions.
 */

const { groupTypes, memberTypes } = DirectoryGroupingConsts;

export class SuggestedSystemUsersCollectionClass extends CollectionModule {
  constructor(Api, idField, sortBy) {
    super(Api, idField, sortBy);
    this.fetchAllSuggestions = null;
  }

  extractModule() {
    const module = super.extractModule();

    module.actions.acceptSuggestions = (...params) => this.acceptSuggestions(...params);
    module.actions.bindSuggestedUsersToUserGroup = (...params) => this.bindSuggestedUsersToUserGroup(...params);
    module.actions.cleanSuggestions = (...params) => this.cleanSuggestions(...params);
    module.actions.refreshCollection = (...params) => this.refreshCollection(...params);
    module.actions.selectItem = (...params) => this.selectItem(...params);
    module.actions.selectAllItems = (...params) => this.selectAllItems(...params);
    module.actions.setFetchSuggestionsTimer = (...params) => this.setFetchSuggestionsTimer(...params);
    module.actions.setPaginationSkip = (...params) => this.setPaginationSkip(...params);
    module.actions.setUserGroupId = (...params) => this.setUserGroupId(...params);
    module.actions.setSuggestions = (...params) => this.setSuggestions(...params);
    module.actions.setSuggestionsTotalCount = (...params) => this.setSuggestionsTotalCount(...params);
    module.actions.unselectAllItems = (...params) => this.unselectAllItems(...params);
    module.actions.unselectItem = (...params) => this.unselectItem(...params);

    module.actions.getSuggestions = (...params) => this.getSuggestions(...params);

    module.mutations.RESET_COLLECTION = (...params) => this.RESET_COLLECTION(...params);
    module.mutations.SELECT_ALL_ITEMS = (...params) => this.SELECT_ALL_ITEMS(...params);
    module.mutations.SET_FETCH_SUGGESTIONS_TIMER = (...params) => this.SET_FETCH_SUGGESTIONS_TIMER(...params);
    module.mutations.SET_USER_GROUP = (...params) => this.SET_USER_GROUP(...params);
    module.mutations.SET_SUGGESTIONS = (...params) => this.SET_SUGGESTIONS(...params);
    module.mutations.SET_SUGGESTIONS_TOTAL_COUNT = (...params) => this.SET_SUGGESTIONS_TOTAL_COUNT(...params);
    module.mutations.UNSELECT_ALL_ITEMS = (...params) => this.UNSELECT_ALL_ITEMS(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();
    state.features.userGroupId = '';
    state.features.allSuggestions = null;
    state.features.fetchSuggestionsTimer = 0;
    return state;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  getSuggestions(context) {
    return context.state.features.allSuggestions;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  acceptSuggestions(_, { id, payload }) {
    return UserGroupApi.acceptSuggestions(id, payload);
  }

  bindSuggestedUsersToUserGroup(context, { addSystemUserIds, removeSystemUserIds }) {
    return context.dispatch('DirectoryGroupingModule/bindAssociations', {
      parentType: groupTypes.userGroup,
      parentId: context.state.features.userGroupId,
      targetType: memberTypes.user,
      selectedTargetIds: addSystemUserIds,
      unselectedTargetIds: removeSystemUserIds,
    }, { root: true });
  }

  cleanSuggestions(context) {
    this.fetchAllSuggestions = null;
    this.setSuggestions(context, { allSuggestions: null });
  }

  /**
   * fetch Collection of suggestions using UserGroupApi and SystemUserApi.
   * This function is handling suggestion pagination by calling suggestion Api
   * and then searching system user properties related to that suggestion.
   * The collection contains system users with op property ( add ,remove) and
   * dynamic properties depending on what rules were selected.
   */
  async fetchCollection(context, { next = false } = {}) {
    return new Promise(async (resolve, reject) => {
      // it is controlling if the process going to take 10 seconds or 0 seconds
      // to accomplish, depends if there are changes left or not.
      const timer = context.state.features.fetchSuggestionsTimer || 0;

      setTimeout(async () => {
        // getting user group id to use it in fetch Suggestions Api call
        const { userGroupId } = context.state.features;

        // default pagination properties
        let { skip } = context.state.features.items.pagination;
        let { limit } = context.state.features.items.pagination;

        let params = {
          skip,
          limit,
        };

        // Calling suggestions api with pagination
        const { results, totalCount } = await UserGroupApi.fetchSuggestions(userGroupId, { params });
        const fetchAllSuggestions = results;

        // saving new skip value to load more items later
        if (next === true) {
          skip += limit;
          this.setPaginationSkip(context, { skip });
        }

        // checking if total Count is being assigned correctly and once
        if (context.state.entities.items.totalCount !== totalCount) {
          this.setSuggestionsTotalCount(context, { totalCount });
        }

        // if we do not need to do more pagination, stop and resolve.
        if (skip > context.state.entities.items.totalCount) {
          resolve();
          return;
        }

        // getting all suggestions object, in this object is stored ALL
        // suggestions in order to use it in selectAllItems() method.
        const suggestions = context.state.features.allSuggestions;

        // checking if allSuggestions property is being assgined once.
        if (suggestions.allSuggestions === null) {
          // default pagination values to fetch all suggestions
          skip = 0;
          limit = 0;

          params = {
            skip,
            limit,
          };

          // calling fetchSuggestions Api
          const data = await UserGroupApi.fetchSuggestions(userGroupId, { params });

          this.setSuggestions(context, { allSuggestions: data.results });
        }

        const systemUsersToAdd = [];
        const systemUsersToRemove = [];

        // mapping suggestions ids to filter system users later
        const ids = fetchAllSuggestions.map(item => ({
          _id: item.object.id,
        }));

        const filter = ids.length ? { or: ids } : undefined;

        // Calling system user api using filtered suggestions ids
        const users = filter ? await SystemUserApi.search(filter, '', undefined, 0, limit)
          .catch((error) => {
            reject(error);
          }) : [];

        if (users
          && users.results) {
          for (const systemUser of users.results) {
            const suggestion = fetchAllSuggestions.find(
              item => item.object.id === systemUser.id,
            )
            || undefined;

            if (suggestion) {
              // getting suggestions operation
              systemUser.suggestion = capitalize(suggestion.op);

              // adding user to array in order to sort it later
              if (suggestion.op === 'add') {
                systemUsersToAdd.push(systemUser);
              } else {
                systemUsersToRemove.push(systemUser);
              }
            }
          }
        }

        // Sort systemUsers by last name in ascending order.
        const compare = (a, b) => {
          if (a.lastname > b.lastname) {
            return 1;
          }
          if (a.lastname < b.lastname) {
            return -1;
          }
          return 0;
        };

        systemUsersToAdd.sort(compare);
        systemUsersToRemove.sort(compare);

        const systemUsers = systemUsersToAdd.concat(systemUsersToRemove);

        // collection payload
        const payload = {
          results: systemUsers,
          totalCount: systemUsers.length || 0,
          totalCountOfAllItems: totalCount || 0,
        };

        context.commit('SET_COLLECTION', payload);

        // reset of timer to avoid 10 seconds delay again in pagination
        this.setFetchSuggestionsTimer(context, { timer: 0 });

        // return of system users ids
        resolve(systemUsers.map(systemUser => systemUser.id));
      }, timer);
    });
  }

  refreshCollection(context) {
    return this.fetchCollection(context);
  }

  selectAllItems({ commit, state }) {
    const { items } = state.entities;
    const { allSuggestions } = state.features.allSuggestions;
    let ids = Object.keys(items.byId);

    // by default, this will select first 50 items, with this code
    // it is selecting ALL items in place.
    if (allSuggestions
      && allSuggestions.length && allSuggestions.length > 50) {
      ids = allSuggestions.map(item => item.object.id);
    }

    commit('SELECT_ALL_ITEMS', ids);
  }

  selectItem({ commit }, { id }) {
    commit('SELECT_ITEM', { id });
  }

  setFetchSuggestionsTimer({ commit }, timer) {
    commit('SET_FETCH_SUGGESTIONS_TIMER', timer);
  }

  setPaginationSkip({ commit }, { skip }) {
    commit('SET_PAGINATION_SKIP', { skip });
  }

  setUserGroupId({ commit }, userGroupId) {
    commit('SET_USER_GROUP', userGroupId);
  }

  setSuggestions({ commit }, allSuggestions) {
    commit('SET_SUGGESTIONS', allSuggestions);
  }

  setSuggestionsTotalCount({ commit }, totalCount) {
    commit('SET_SUGGESTIONS_TOTAL_COUNT', totalCount);
  }

  unselectItem({ commit }, { id }) {
    commit('UNSELECT_ITEM', { id });
  }

  unselectAllItems({ commit, state }) {
    const { items } = state.entities;
    const { allSuggestions } = state.features.allSuggestions;
    let ids = Object.keys(items.byId);

    // by default, this will select first 50 items, with this code
    // it is selecting ALL items in place.
    if (allSuggestions
      && allSuggestions.length && allSuggestions.length > 50) {
      ids = allSuggestions.map(item => item.object.id);
    }

    commit('UNSELECT_ALL_ITEMS', ids);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SELECT_ALL_ITEMS(state, ids) {
    state.features.selectedItems.ids = ids;
    state.features.unselectedItems.ids = [];
  }

  SET_FETCH_SUGGESTIONS_TIMER(state, payload) {
    state.features.fetchSuggestionsTimer = payload;
  }

  SET_USER_GROUP(state, payload) {
    state.features.userGroupId = payload;
  }

  SET_SUGGESTIONS(state, payload) {
    state.features.allSuggestions = payload;
  }

  SET_SUGGESTIONS_TOTAL_COUNT(state, payload) {
    state.entities.items.totalCount = payload;
  }

  UNSELECT_ALL_ITEMS(state, ids) {
    state.features.selectedItems.ids = [];
    state.features.unselectedItems.ids = ids;
  }
}

export default new SuggestedSystemUsersCollectionClass(SystemUserApi, '_id', '').extractModule();
